import {
  Component,
  OnInit,
  Input,
  ViewChild,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { Config } from '@core/config';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CardModel, Image } from '@shared/models/card.model';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { GalleryComponent } from '@shared/components/gallery/gallery.component';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CardService } from '@app/cards/card.service';
import { CookieService } from 'ngx-cookie-service';
import {
  getCategoryName,
  getActualImage,
  onImageError,
  determineSubCateg,
} from '@app/@shared/tools/tools';
import { GAService } from '@app/@shared/services/google-analytics.service';
import { translate } from '@angular/localize/src/utils';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'card-images',
  templateUrl: './card-images.component.html',
  styleUrls: ['./card-images.component.scss'],
})
export class CardImagesComponent implements OnInit, AfterViewInit {
  @ViewChild('car') carousel: CdkVirtualScrollViewport;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @Input() card: CardModel;
  public current: number;
  public title: string[];
  public isMobile: boolean;
  public currentScreenWidth: number;
  public shareUrl: string;
  public fbApiKey: string;
  public sources: { name: string; url: string }[];
  public tweetText: string;
  public rating: number;
  public voteCount: number;
  public iframeUrl: SafeResourceUrl;

  constructor(
    public dialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private cardService: CardService,
    private cookieService: CookieService,
    private gaService: GAService,
    private _sanitizer: DomSanitizer
  ) {
    this.current = 0;
    this.isMobile =
      /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    this.shareUrl = encodeURIComponent(window.location.href);
    this.fbApiKey = Config.facebookAppId;
  }

  ngOnInit(): void {
    this.onResize();
    this.getTitle();
    this.setSource();
    this._generateTweetText();
    this.setRatingOnInit();
    this.sendPoiGaEvent();
  }

  ngAfterViewInit() {
    this.iframeUrl =
      this.card.media?.length ?? 0 > 0
        ? this.sanitizeVideoUrl(
            this.card.media[this.current - this.card.images.length].url
          )
        : null;
  }

  /**
   * Set the current @this _currentScreenWidth attribute with actual screen width device.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.isMobile) this.currentScreenWidth = window.screen.width;
    else this.currentScreenWidth = window.innerWidth;
  }

  /**
   * Define the correct title.
   */
  public getTitle() {
    try {
      const cat = Object.values(Config.categories).map((c) => c.name)[
        this.card.category
      ];
      this.title = [cat];
      if (this.card.category === 1) {
        const type =
          // tslint:disable-next-line:no-bitwise
          (this.card.circuitType & 1) > 0
            ? Config.circuit[0].name
            : // tslint:disable-next-line:no-bitwise
            (this.card.circuitType & 2) > 0
            ? Config.circuit[1].name
            : // tslint:disable-next-line:no-bitwise
            (this.card.circuitType & 4) > 0
            ? Config.circuit[2].name
            : // tslint:disable-next-line:no-bitwise
            (this.card.circuitType & 8) > 0
            ? Config.circuit[3].name
            : // tslint:disable-next-line:no-bitwise
            (this.card.circuitType & 16) > 0
            ? Config.circuit[4].name
            : Config.circuit[2].name;
        if (type !== undefined) {
          this.title.push(type);
        }
      } else if (this.card.subcategory.name !== undefined) {
        this.title.push(this.card?.subcategory.name);
      }
    } catch (_e) {}
  }

  public setSource(): void {
    if (this.card.category === 2) {
      if (this.card.subcategory.id == '38') {
        this.sources = [
          {
            name: "Département du Val d'Oise - Environnement, Développement Durable et de l'Agriculture",
            url: 'https://valdoise.fr/144-l-agriculture.htm',
          },
        ];
      } else {
        this.sources = [
          {
            name: "Val d'Oise Tourisme - Apidae",
            url: 'https://www.valdoise-tourisme.com/',
          },
        ];
      }
    } else if (this.card.category === 3) {
      this.sources = [{ name: this.card.deliveredBy, url: null }];
    } else {
      this.sources = this.card.group.collectivities;
    }
  }

  public setRatingOnInit(): void {
    this.rating = this.card?.votes.avg;
    this.voteCount = this.card?.votes.count;
  }

  public expandMore(): void {
    this.trigger.openMenu();
  }

  public navForward(): void {
    this.current =
      this.current + 1 < this.imageMediaLength() ? this.current + 1 : 0;
    if (this.current >= this.card.images.length) {
      this.iframeUrl = this.sanitizeVideoUrl(
        this.card.media[this.current - this.card.images.length].url
      );
    }
  }

  public navBackward(): void {
    this.current =
      this.current - 1 >= 0 ? this.current - 1 : this.imageMediaLength() - 1;
    if (this.current >= this.card.images.length) {
      this.iframeUrl = this.sanitizeVideoUrl(
        this.card.media[this.current - this.card.images.length].url
      );
    }
  }

  public openGallery(i: number) {
    this.dialog.open(GalleryComponent, {
      data: {
        images: this.card.images,
        media: this.card.media,
        i,
      },
      backdropClass: 'gallery-backdrop',
    });
  }

  sanitizeVideoUrl(videoURL: string) {
    return this._sanitizer.bypassSecurityTrustResourceUrl(
      videoURL.replace('watch?v=', 'embed/')
    );
  }

  public checkIfDateIsInterval(dateStart: string, dateEnd?: string): boolean {
    // Returns true if start date and end date are different

    const start = new Date(dateStart);
    const end = new Date(dateEnd);
    return (
      start.getDate() != end.getDate() ||
      start.getMonth() != end.getMonth() ||
      start.getFullYear() != end.getFullYear()
    );
  }

  public checkIfDateIsCurrent(dateStart: string, dateEnd?: string): boolean {
    // Returns true if current day date == start date, or current day comprised between start date and end date

    const current = new Date();
    const start = new Date(dateStart);
    if (dateEnd === undefined) {
      return (
        start.getDate() == current.getDate() &&
        start.getMonth() == current.getMonth() &&
        start.getFullYear() == current.getFullYear()
      );
    } else {
      const end = new Date(dateEnd);
      return start <= current && current <= end;
    }
  }

  public checkIfDateIsCurrentOrFuture(
    dateStart: string,
    dateEnd?: string
  ): boolean {
    // Returns true if current day date <= start date, or if current day date is anywhere before end date

    const current = new Date();
    const start = new Date(dateStart);
    if (dateEnd === undefined) {
      return start >= current;
    } else {
      const end = new Date(dateEnd);
      return end >= current;
    }
  }

  /**
   * Format the given date
   * @param format 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
   * @returns A formated date.
   */
  public formatDate(
    dateIn: string,
    format: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
  ): string {
    const date = new Date(dateIn);
    return `${date.getDate()} ${date.toLocaleString('fr-FR', {
      month: format,
    })}`;
  }

  /**
   * Get a stringified date and check if this date is valid.
   * We use that because some data are wrongly managed.
   * @returns true when data is correct, else false.
   */
  public checkSchedule(dateIn: string): boolean {
    const date = new Date(dateIn);
    return !(date.getHours() === 0 && date.getMinutes() === 0);
  }

  /**
   * Get a stringified date and return the schedule value of it.
   * @returns shcdule data.
   */
  public formatSchedule(dateIn: string): string {
    const date = new Date(dateIn);
    const hours =
      date.getHours() < 10
        ? `0${date.getHours().toFixed()}`
        : date.getHours().toFixed();
    const minutes =
      date.getMinutes() < 10
        ? `0${date.getMinutes().toFixed()}`
        : date.getMinutes().toFixed();
    return `${hours}h${minutes}`;
  }

  public async goBack() {
    const mapUrl = window.sessionStorage.getItem('lastMap');
    window.sessionStorage.removeItem('lastMap');
    await this.router.navigate(mapUrl.split('/'), {
      relativeTo: this.activateRoute.parent,
    });
  }

  /**
   * Rate a card and store it
   */
  public async rateCard(id: string, rating: number): Promise<void> {
    try {
      const updatedCard = await this.cardService.setNewRating(
        id,
        this.cookieService.get('userUuid'),
        rating
      );
      this.sendVoteGaEvent(rating);
      this.rating = updatedCard.votes.avg;
      this.voteCount = updatedCard.votes.count;
    } catch (e: any) {}
  }

  /**
   * Check if the actual card dispose of rating system or not.
   */
  public canShowRating(): boolean {
    return !(this.card.category === 3 || this.card.category === 2);
  }

  /**
   * Allow us to get the current image to get.
   */
  public getCurrentImage(categ: number, img: Image | null = null): string {
    return getActualImage(categ, img?.url);
  }

  /**
   * Used on error option from image.
   */
  public manageImageError(img: HTMLImageElement): void {
    onImageError(this.card.category, img);
  }

  public imageMediaLength(): number {
    return (
      (this.card.images ? this.card.images.length : 0) +
      (this.card.media ? this.card.media.length : 0)
    );
  }

  /**
   * Col span setter.
   */
  public setColSpanNumber(index: number): number {
    if (this.imageMediaLength() === 1) return 4;
    if (index === 0) return 2;
    if (this.imageMediaLength() === 2 || this.imageMediaLength() === 3)
      return 2;
    if (index === 3 && this.imageMediaLength() === 4) return 2;
    return 1;
  }

  /**
   * Row span setting.
   */
  public setRowSpanNumber(index: number): number {
    if (this.imageMediaLength() === 1) return 2;
    if (index === 0) return 2;
    if (this.imageMediaLength() === 2) return 2;
    return 1;
  }

  /**
   * Check if pdf file is stored in data.
   * @returns {boolean}
   */
  public hasPdf(): boolean {
    return this.card.pathPdf != null && this.card.pathPdf !== '';
  }

  /**
   * Check if PDF exists and could be display by the current POI.
   */
  public shouldDisplayPrint(): boolean {
    return [0, 1].includes(this.card.category);
  }

  /**
   * Redirect to view print.
   */
  public goPrint() {
    this.sendUpGaEventPrint();
    this.router.navigateByUrl(`/print/${this.card.id}`);
  }

  /**
   * Allow us to send share event.
   * @param shareType 0 = facebook, 1 = twitter.
   */
  public sendShareGaEvent(shareType: number = 0): void {
    let label = '';
    switch (shareType) {
      case 0:
        label = 'Partage Facebook';
        break;
      case 1:
        label = 'Partage Twitter';
        break;
      default:
        label = 'Partage Facebook';
    }
    this.gaService.sendEvent(
      getCategoryName(this.card),
      'Partage',
      `${label} ${this.card.innerID ?? this.card.old_id}`
    );
  }

  public sendGenericShareGaEvent(): void {
    this.gaService.sendEvent(
      getCategoryName(this.card),
      'Partage',
      `Fiche ${this.card.innerID ?? this.card.old_id}`
    );
  }

  /**
   * PRINT GA EVENT
   */
  public sendUpGaEventPrint() {
    this.gaService.sendEvent(
      getCategoryName(this.card),
      'Impression',
      this.card.title
    );
  }

  /**
   * We use this to send GA Analytics for PDF CLICK.
   */
  public sendUpGaEventPdf() {
    this.gaService.sendEvent(
      `PDF`,
      'Téléchargement',
      `Fiche ${this.card.innerID ?? this.card.old_id}`
    );
  }

  /**
   * We use this to send GA Analytics for Gpx click.
   */
  public sendUpGaEventGpx() {
    this.gaService.sendEvent(
      `GPX`,
      'Téléchargement',
      `Fiche ${this.card.innerID ?? this.card.old_id}`
    );
  }

  /**
   * Translate tweet text.
   */
  private _generateTweetText() {
    this.tweetText = this.translateService.instant('TweetText', {
      title: this.card?.title,
    });
  }

  /**
   * Send event of current score.
   * Used from another public function in this component.
   * @param currentScore Score clicked by user
   */
  private sendVoteGaEvent(currentScore: number): void {
    this.gaService.sendEvent(
      getCategoryName(this.card),
      'Vote',
      `Fiche ${this.card.innerID ?? this.card.old_id}`,
      currentScore
    );
  }

  /**
   * Suicide.
   * Du.
   * Code.
   * Je ne peux.
   * Tolérer un tel.
   * Code.
   * Bip bip boup.
   */
  private sendPoiGaEvent(): void {
    if (this.card.category !== 1) {
      this.gaService.sendEvent(
        determineSubCateg(this.card),
        'Fiche',
        `Fiche ${this.card.innerID ?? this.card.old_id}`
      );
    } else {
      // I'm so sorry about that
      switch (this.card.circuitType) {
        case 1: {
          this.gaService.sendEvent(
            'A pied',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 2: {
          this.gaService.sendEvent(
            'A vélo - VTT',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 6: {
          this.gaService.sendEvent(
            'A vélo - VTT',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          this.gaService.sendEvent(
            'A vélo - VTC',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 10: {
          this.gaService.sendEvent(
            'A vélo - VTT',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          this.gaService.sendEvent(
            'A vélo - sur route',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 14: {
          this.gaService.sendEvent(
            'A vélo - VTT',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          this.gaService.sendEvent(
            'A vélo - VTC',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          this.gaService.sendEvent(
            'A vélo - sur route',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 4: {
          this.gaService.sendEvent(
            'A vélo - VTC',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 12: {
          this.gaService.sendEvent(
            'A vélo - VTC',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          this.gaService.sendEvent(
            'A vélo - sur route',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 8: {
          this.gaService.sendEvent(
            'A vélo - sur route',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 16: {
          this.gaService.sendEvent(
            'A cheval',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        case 32: {
          this.gaService.sendEvent(
            'Trail',
            'Fiche',
            `Fiche ${this.card.innerID ?? this.card.old_id}`
          );
          break;
        }
        default:
          break;
      }
    }
  }
}
